/**
 * ConsignmentRMAAdminTab
 *
 * Mirrors RMAAdminTab for consignment returns.
 * Uses approve_consignment_rma RPC which chains the return to
 * "תעודת החזרה מלקוח קונסגנציה" (prefix RTVCO).
 */

import {
  useEffect, useState, useCallback, useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { Loader as Loader2, CircleAlert as AlertCircle, RotateCcw, X, CircleCheck as CheckCircle2, Circle as XCircle, Clock, RefreshCw, Package, User, ChevronRight, ThumbsUp, ThumbsDown, SlidersHorizontal, Boxes } from 'lucide-react';
import { supabase }       from '@/lib/supabase';
import { usePermissions } from '@/hooks/usePermissions';
import { ReturnPrintActions, SelectionBar, RowCheckbox } from '@/components/inventory/IncomingDocsPrint';
import type { PrintableReturn } from '@/lib/incomingDocsExport';

// ─── Types ────────────────────────────────────────────────────────────────────

type RMAStatus = 'pending' | 'approved' | 'partial' | 'rejected';

interface RMALine {
  id:            string;
  product_id:    string;
  product_name:  string;
  product_sku:   string;
  requested_qty: number;
  received_qty:  number | null;
  unit_price:    number;
  return_reason: string | null;
}

interface RMARequest {
  id:            string;
  short_id:      string;
  status:        RMAStatus;
  return_reason: string | null;
  admin_notes:   string | null;
  total_lines:   number;
  created_at:    string;
  reviewed_at:   string | null;
  client_id:     string;
  client_name:   string;
  lines:         RMALine[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PRINT_TITLE = 'החזרת קונסגנציה נכנסת';
const FILE_PREFIX = 'החזרת קונסגנציה';

function toPrintable(rma: RMARequest): PrintableReturn {
  return {
    id:          rma.id,
    shortId:     rma.short_id,
    statusLabel: STATUS_META[rma.status]?.label ?? rma.status,
    isOpen:      rma.status === 'pending',
    createdAt:   rma.created_at,
    clientName:  rma.client_name,
    reason:      rma.return_reason,
    adminNotes:  rma.admin_notes,
    lines: rma.lines.map(l => ({
      name:      l.product_name,
      sku:       l.product_sku,
      requested: l.requested_qty,
      received:  l.received_qty,
      unitPrice: l.unit_price,
      reason:    l.return_reason,
    })),
  };
}

const fmtPrice = (n: number) =>
  `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleDateString('he-IL', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const shortId = (id: string) => id.slice(-8).toUpperCase();

// ─── Status meta ──────────────────────────────────────────────────────────────

const STATUS_META: Record<RMAStatus, {
  label: string; icon: React.ReactNode; pill: string; row: string;
}> = {
  pending: {
    label: 'ממתין לטיפול',
    icon:  <Clock             className="w-3.5 h-3.5" />,
    pill:  'bg-amber-100  text-amber-700  border-amber-200',
    row:   '',
  },
  approved: {
    label: 'אושר',
    icon:  <CheckCircle2      className="w-3.5 h-3.5" />,
    pill:  'bg-teal-100   text-teal-700   border-teal-200',
    row:   'bg-teal-50/30',
  },
  partial: {
    label: 'אושר חלקית',
    icon:  <SlidersHorizontal className="w-3.5 h-3.5" />,
    pill:  'bg-blue-100   text-blue-700   border-blue-200',
    row:   'bg-blue-50/30',
  },
  rejected: {
    label: 'נדחה',
    icon:  <XCircle           className="w-3.5 h-3.5" />,
    pill:  'bg-red-100    text-red-600    border-red-200',
    row:   'opacity-60',
  },
};

function StatusBadge({ status }: { status: RMAStatus }) {
  const m = STATUS_META[status] ?? STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                      text-[11px] font-semibold border leading-none ${m.pill}`}>
      {m.icon}
      {m.label}
    </span>
  );
}

// ─── Received qty cell ────────────────────────────────────────────────────────

function ReceivedQtyCell({
  line, value, onChange, editable,
}: {
  line:     RMALine;
  value:    number;
  onChange: (lineId: string, qty: number) => void;
  editable: boolean;
}) {
  const reduced  = value < line.requested_qty;
  const rejected = value === 0;

  return (
    <div className="flex flex-col items-end gap-1 min-w-[120px]">
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-gray-400 font-medium">התקבל:</span>
        {editable ? (
          <input type="number" min={0} max={line.requested_qty} value={value}
            onChange={e => {
              const v = parseInt(e.target.value, 10);
              if (!isNaN(v) && v >= 0) onChange(line.id, v);
            }}
            className={`w-16 text-center text-sm font-bold rounded-lg border
                        px-1.5 py-1 focus:outline-none focus:ring-2 transition-colors
                        ${rejected
                          ? 'border-red-300   bg-red-50   text-red-700   focus:ring-red-200'
                          : reduced
                            ? 'border-amber-300 bg-amber-50 text-amber-700 focus:ring-amber-200'
                            : 'border-gray-200  bg-white   text-gray-900  focus:ring-teal-300'
                        }`} />
        ) : (
          <span className={`text-sm font-bold tabular-nums
                            ${rejected ? 'text-red-600' : reduced ? 'text-amber-600' : 'text-gray-800'}`}>
            {value.toLocaleString('he-IL')}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-gray-400 font-medium">ביקש:</span>
        <span className="text-[11px] text-gray-500 tabular-nums">
          {line.requested_qty.toLocaleString('he-IL')}
        </span>
      </div>
      {rejected && (
        <span className="text-[9px] font-semibold text-red-600 bg-red-50
                         border border-red-200 px-1.5 py-0.5 rounded-full">נדחה</span>
      )}
      {!rejected && reduced && (
        <span className="text-[9px] font-semibold text-amber-600 bg-amber-50
                         border border-amber-200 px-1.5 py-0.5 rounded-full">חלקי</span>
      )}
    </div>
  );
}

// ─── ReviewDrawer ─────────────────────────────────────────────────────────────

function ReviewDrawer({
  rma, onClose, onApproved, canEdit,
}: {
  rma:        RMARequest;
  onClose:    () => void;
  onApproved: (updatedRma: Partial<RMARequest> & { id: string }) => void;
  canEdit:    boolean;
}) {
  const isPending = rma.status === 'pending';
  const editable  = isPending && canEdit;

  const [receivedMap, setReceivedMap] = useState<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const l of rma.lines) m[l.id] = l.received_qty !== null ? l.received_qty : l.requested_qty;
    return m;
  });

  const [adminNotes, setAdminNotes] = useState(rma.admin_notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  const handleQtyChange = useCallback((lineId: string, qty: number) => {
    setReceivedMap(prev => ({ ...prev, [lineId]: qty }));
  }, []);

  const handleRejectAll = useCallback(() => {
    const m: Record<string, number> = {};
    for (const l of rma.lines) m[l.id] = 0;
    setReceivedMap(m);
  }, [rma.lines]);

  const handleApprove = useCallback(async () => {
    setSubmitting(true);
    setError(null);

    const reviewedLines = rma.lines.map(l => ({
      rma_line_id:  l.id,
      received_qty: receivedMap[l.id] ?? 0,
    }));

    const { data, error: rpcErr } = await supabase.rpc('approve_consignment_rma', {
      p_rma_id:        rma.id,
      p_reviewed_lines: reviewedLines,
      p_admin_notes:   adminNotes.trim() || null,
    });

    if (rpcErr) {
      setError(rpcErr.message);
      setSubmitting(false);
      return;
    }

    const result = data as {
      rma_id: string; final_status: string;
      lines_received: number; lines_rejected: number;
    };

    onApproved({
      id:          rma.id,
      status:      result.final_status as RMAStatus,
      admin_notes: adminNotes.trim() || null,
      reviewed_at: new Date().toISOString(),
    });
  }, [rma, receivedMap, adminNotes, onApproved]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const totalReceived = rma.lines.reduce((s, l) => s + (receivedMap[l.id] ?? 0), 0);
  const anyModified   = rma.lines.some(l => (receivedMap[l.id] ?? l.requested_qty) !== l.requested_qty);

  return createPortal(
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-lg
                      bg-white shadow-2xl" dir="rtl">

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-5 pb-4
                        border-b border-gray-100">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Boxes className="w-3.5 h-3.5 text-teal-600" />
              <span className="text-[10px] font-bold text-teal-700 uppercase">קונסגנציה</span>
              <span className="font-mono text-xs text-gray-400 font-semibold">#{rma.short_id}</span>
              <StatusBadge status={rma.status} />
            </div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400 shrink-0" />
              {rma.client_name}
            </h2>
            <p className="text-xs text-gray-400">{fmtDateTime(rma.created_at)}</p>
          </div>
          <button onClick={onClose}
            className="shrink-0 ml-3 p-1.5 rounded-lg text-gray-400
                       hover:bg-gray-100 hover:text-gray-700 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-gray-100 bg-white shrink-0">
          <ReturnPrintActions returns={[toPrintable(rma)]} title={PRINT_TITLE} filePrefix={FILE_PREFIX} />
        </div>

        {/* Return reason */}
        {rma.return_reason && (
          <div className="mx-5 mt-3 px-3 py-2.5 bg-amber-50 border border-amber-200
                          rounded-xl text-xs text-amber-800">
            <span className="font-semibold">סיבת ההחזרה: </span>
            {rma.return_reason}
          </div>
        )}

        {/* Lines */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          <div className="flex items-center justify-between text-[10px]
                          font-semibold text-gray-400 uppercase tracking-wide
                          px-3 pb-2">
            <span>מוצר</span>
            <span>כמויות</span>
          </div>

          {rma.lines.map(line => {
            const val = receivedMap[line.id] ?? line.requested_qty;
            return (
              <div key={line.id}
                className={`flex items-start gap-3 px-3 py-3 rounded-xl border transition-colors
                            ${val === 0
                              ? 'bg-red-50/60 border-red-100'
                              : val < line.requested_qty
                                ? 'bg-amber-50/60 border-amber-100'
                                : 'bg-gray-50 border-gray-100'
                            }`}>
                <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center shrink-0">
                  <Package className="w-4 h-4 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-tight truncate">
                    {line.product_name}
                  </p>
                  <p className="text-[11px] text-gray-400 font-mono mt-0.5">{line.product_sku}</p>
                  {line.unit_price > 0 && (
                    <p className="text-[11px] text-gray-500 mt-0.5">{fmtPrice(line.unit_price)} / יח׳</p>
                  )}
                  {line.return_reason && (
                    <p className="text-[10px] text-amber-700 mt-1 italic leading-tight">
                      {line.return_reason}
                    </p>
                  )}
                </div>
                <ReceivedQtyCell line={line} value={val} onChange={handleQtyChange} editable={editable} />
              </div>
            );
          })}

          <div className="flex items-center justify-between px-3 pt-2 border-t border-gray-100 mt-2">
            <span className="text-xs text-gray-500 font-medium">סה״כ יחידות להחזרה:</span>
            <span className="text-sm font-bold text-gray-900">
              {totalReceived.toLocaleString('he-IL')} יח׳
            </span>
          </div>

          {anyModified && editable && (
            <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50
                            border border-amber-200 rounded-xl text-xs text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p>הכמויות שונו ביחס לבקשת ההחזרה המקורית.</p>
            </div>
          )}
        </div>

        {/* Admin notes */}
        {editable && (
          <div className="px-5 pb-3">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              הערות פנימיות (לא גלויות ללקוח)
            </label>
            <textarea rows={2} value={adminNotes}
              onChange={e => setAdminNotes(e.target.value)}
              placeholder="הוסף הערה אופציונלית…"
              className="w-full text-sm rounded-xl border border-gray-200 px-3 py-2
                         focus:outline-none focus:ring-2 focus:ring-teal-300
                         resize-none placeholder:text-gray-300" />
          </div>
        )}

        {!editable && rma.admin_notes && (
          <div className="mx-5 mb-3 px-3 py-2.5 bg-gray-50 border border-gray-200
                          rounded-xl text-xs text-gray-700">
            <span className="font-semibold">הערות: </span>{rma.admin_notes}
          </div>
        )}

        {error && (
          <div className="mx-5 mb-3 flex items-start gap-2 px-3 py-2.5 bg-red-50
                          border border-red-200 rounded-xl text-xs text-red-700">
            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {/* Footer actions */}
        {editable && (
          <div className="flex items-center gap-3 px-5 pb-5 pt-2 border-t border-gray-100">
            <button onClick={handleRejectAll} disabled={submitting}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl
                         border border-red-200 text-red-600 text-sm font-semibold
                         hover:bg-red-50 transition-colors disabled:opacity-50">
              <ThumbsDown className="w-3.5 h-3.5" />
              דחה הכל
            </button>
            <button onClick={handleApprove} disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2
                         py-2.5 rounded-xl text-sm font-bold text-white
                         bg-teal-600 hover:bg-teal-700 active:bg-teal-800
                         transition-colors disabled:opacity-60 active:scale-[.98]">
              {submitting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <ThumbsUp className="w-4 h-4" />
              }
              {submitting ? 'מאשר…' : 'אשר וקבל סחורה קונסגנציה'}
            </button>
          </div>
        )}

        {!editable && rma.reviewed_at && (
          <div className="px-5 pb-5 text-center text-xs text-gray-400">
            טופל ב-{fmtDateTime(rma.reviewed_at)}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface ConsignmentRMAAdminTabProps {
  companyId: string;
}

export function ConsignmentRMAAdminTab({ companyId }: ConsignmentRMAAdminTabProps) {
  const { can } = usePermissions();
  const canEdit = can({ module: 'inventory', level: 'editor' });

  const [rmas,         setRmas]         = useState<RMARequest[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [selected,     setSelected]     = useState<RMARequest | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<RMAStatus | 'all'>('pending');
  const [toast,        setToast]        = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const fetchRmas = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: rmaData, error: rmaErr } = await supabase
        .from('rma_requests')
        .select(`
          id, status, return_reason, admin_notes, total_lines,
          created_at, reviewed_at, client_id,
          inventory_clients ( name )
        `)
        .eq('company_id', companyId)
        .eq('order_type', 'consignment')
        .order('created_at', { ascending: false });

      if (rmaErr) throw rmaErr;

      const filteredRmaData = rmaData ?? [];
      const rmaIds = filteredRmaData.map((r: any) => r.id);
      let linesMap: Record<string, RMALine[]> = {};

      if (rmaIds.length > 0) {
        const { data: lineData, error: lineErr } = await supabase
          .from('rma_lines')
          .select(`
            id, rma_id, product_id, requested_qty, received_qty,
            unit_price, return_reason,
            inventory_products ( name, sku )
          `)
          .in('rma_id', rmaIds);

        if (lineErr) throw lineErr;

        for (const l of (lineData ?? []) as any[]) {
          const rmaId = l.rma_id as string;
          if (!linesMap[rmaId]) linesMap[rmaId] = [];
          linesMap[rmaId].push({
            id:            l.id,
            product_id:    l.product_id,
            product_name:  (l.inventory_products as any)?.name ?? 'מוצר לא זמין',
            product_sku:   (l.inventory_products as any)?.sku  ?? '',
            requested_qty: l.requested_qty,
            received_qty:  l.received_qty,
            unit_price:    Number(l.unit_price ?? 0),
            return_reason: l.return_reason ?? null,
          });
        }
      }

      const mapped: RMARequest[] = filteredRmaData.map((r: any) => ({
        id:            r.id,
        short_id:      shortId(r.id),
        status:        r.status as RMAStatus,
        return_reason: r.return_reason ?? null,
        admin_notes:   r.admin_notes   ?? null,
        total_lines:   r.total_lines   ?? 0,
        created_at:    r.created_at,
        reviewed_at:   r.reviewed_at   ?? null,
        client_id:     r.client_id,
        client_name:   (r.inventory_clients as any)?.name ?? 'לקוח לא ידוע',
        lines:         linesMap[r.id] ?? [],
      }));

      setRmas(mapped);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת בקשות ההחזרה');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchRmas(); }, [fetchRmas]);

  const handleApproved = useCallback((update: Partial<RMARequest> & { id: string }) => {
    setRmas(prev => prev.map(r => r.id === update.id ? { ...r, ...update } : r));
    setSelected(prev => prev && prev.id === update.id ? { ...prev, ...update } : prev);
    const statusLabel = STATUS_META[update.status as RMAStatus]?.label ?? update.status;
    setToast({ kind: 'success', msg: `בקשת ההחזרה ${statusLabel} בהצלחה` });
    setTimeout(() => setToast(null), 4000);
    setSelected(null);
  }, []);

  const displayed = useMemo(() =>
    filterStatus === 'all' ? rmas : rmas.filter(r => r.status === filterStatus),
    [rmas, filterStatus],
  );

  const toggleChecked = (id: string) =>
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allVisibleChecked = displayed.length > 0 && displayed.every(r => checked.has(r.id));
  const toggleAllVisible = () =>
    setChecked(prev => {
      const next = new Set(prev);
      if (allVisibleChecked) displayed.forEach(r => next.delete(r.id));
      else displayed.forEach(r => next.add(r.id));
      return next;
    });

  const checkedPrintable = useMemo(
    () => rmas.filter(r => checked.has(r.id)).map(toPrintable),
    [rmas, checked],
  );

  const pendingCount = rmas.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-4 pb-10" dir="rtl">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2.5
                         px-4 py-3 rounded-2xl shadow-lg text-sm font-semibold text-white
                         ${toast.kind === 'success' ? 'bg-teal-600' : 'bg-red-600'}`}>
          {toast.kind === 'success'
            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
            : <AlertCircle  className="w-4 h-4 shrink-0" />
          }
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <RotateCcw className="w-5 h-5 text-teal-600" />
          <h2 className="text-base font-bold text-gray-900">החזרות קונסגנציה (RMA)</h2>
          {pendingCount > 0 && (
            <span className="text-xs font-bold px-2 py-0.5 rounded-full
                             bg-amber-100 text-amber-700 border border-amber-200">
              {pendingCount} ממתינות
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
            {(['all', 'pending', 'approved', 'partial', 'rejected'] as const).map(s => (
              <button key={s} onClick={() => setFilterStatus(s)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors
                            ${filterStatus === s
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                            }`}>
                {s === 'all' ? 'הכל' : STATUS_META[s].label}
              </button>
            ))}
          </div>
          <button onClick={fetchRmas} disabled={loading}
            className="p-2 rounded-xl border border-gray-200 text-gray-500
                       hover:bg-gray-50 transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
          <span className="text-sm">טוען בקשות החזרה קונסגנציה…</span>
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50
                        border border-red-200 rounded-2xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">שגיאה בטעינת בקשות ההחזרה</p>
            <p className="text-xs mt-0.5 text-red-500">{error}</p>
          </div>
        </div>
      )}

      {!loading && !error && displayed.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24 gap-5">
          <div className="w-20 h-20 rounded-3xl bg-teal-50 flex items-center justify-center">
            <RotateCcw className="w-10 h-10 text-teal-200" />
          </div>
          <div className="text-center space-y-1.5">
            <h3 className="text-lg font-bold text-gray-800">
              {filterStatus === 'pending' ? 'אין החזרות ממתינות' : 'אין בקשות'}
            </h3>
            <p className="text-sm text-gray-500 max-w-xs">
              {filterStatus === 'pending'
                ? 'כל בקשות החזרת הקונסגנציה הפתוחות יופיעו כאן.'
                : 'לא נמצאו בקשות בסטטוס הנבחר.'
              }
            </p>
          </div>
        </div>
      )}

      <SelectionBar count={checkedPrintable.length} onClear={() => setChecked(new Set())}>

        <ReturnPrintActions returns={checkedPrintable} title={PRINT_TITLE} filePrefix="החזרות קונסגנציה" />

      </SelectionBar>


      {!loading && !error && displayed.length > 0 && (
        <div className="rounded-2xl border border-gray-200 overflow-hidden">
          <div className="hidden md:flex items-center bg-teal-50/40 border-b border-gray-100">
          <div className="pr-4 pl-1 flex items-center">
            <RowCheckbox checked={allVisibleChecked} onChange={toggleAllVisible} label="בחר הכל" />
          </div>
          <div className="flex-1 grid grid-cols-[120px_1fr_1fr_80px_140px_48px]
                          text-[11px] font-semibold text-gray-400 uppercase tracking-wide
                          px-4 py-2.5">
            <span>מזהה</span>
            <span>לקוח</span>
            <span>סיבה</span>
            <span className="text-center">פריטים</span>
            <span>סטטוס</span>
            <span />
          </div>
          </div>

          <div className="divide-y divide-gray-100">
            {displayed.map(rma => {
              const meta = STATUS_META[rma.status];
              return (
                <div key={rma.id}
                  className={`flex items-center ${checked.has(rma.id) ? 'bg-teal-50/70' : meta.row}`}>
                <div className="pr-4 pl-1 flex items-center">
                  <RowCheckbox checked={checked.has(rma.id)} onChange={() => toggleChecked(rma.id)}
                    label={`בחר החזרה ${rma.short_id}`} />
                </div>
                <button onClick={() => setSelected(rma)}
                  className="flex-1 min-w-0 text-right flex md:grid md:grid-cols-[120px_1fr_1fr_80px_140px_48px]
                              items-center gap-3 px-4 py-3.5 transition-colors
                              hover:bg-teal-50/30 active:bg-gray-100 text-sm">
                  <div className="shrink-0">
                    <p className="font-mono text-xs font-semibold text-gray-700">#{rma.short_id}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{fmtDate(rma.created_at)}</p>
                  </div>
                  <div className="flex-1 min-w-0 md:flex-none">
                    <p className="font-semibold text-gray-900 truncate">{rma.client_name}</p>
                  </div>
                  <div className="hidden md:block min-w-0">
                    <p className="text-xs text-gray-500 truncate">{rma.return_reason ?? '—'}</p>
                  </div>
                  <div className="hidden md:flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-700">{rma.total_lines}</span>
                  </div>
                  <div className="shrink-0"><StatusBadge status={rma.status} /></div>
                  <div className="hidden md:flex items-center justify-center text-gray-300">
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selected && (
        <ReviewDrawer
          rma={selected}
          onClose={() => setSelected(null)}
          onApproved={handleApproved}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
